import React, {FunctionComponent, useEffect, useState} from "react";
import CreatableReactSelect from "react-select/creatable";
import ReactSelect, {GroupBase, InputActionMeta, OptionsOrGroups, StylesConfig} from "react-select";
import {useTranslation} from "react-i18next";
import {Color} from "../../../../utils/color";
import {ReactSelectPropsWithOverwrite} from "../../../../utils/input";
import {TranslationLibFile, TranslationLibKey} from "../../../../utils/locale";
import {customTheme, getSelectedOption, selectComponents, SelectOption, selectStyles, ValidationInput} from "./commonSelect";

export type SelectAutocompleteProps = Omit<ReactSelectPropsWithOverwrite<{
    value?: string | number | boolean;
    options: OptionsOrGroups<SelectOption, GroupBase<SelectOption>>;
    placeholder?: string;
    onChange?: (option?: SelectOption) => void;
    onInput?: (e: React.FormEvent<HTMLInputElement>) => void;
    onInvalid?: (e: React.FormEvent<HTMLInputElement>) => void;
    onCreate?: (value: string) => void;
    disabled?: boolean;
    creatable?: boolean;
    clearable?: boolean;
    small?: boolean;
    resetInputOnSelect?: boolean;
}>, "onCreateOption" | "isDisabled" | "isClearable" | "components" | "styles" | "noOptionsMessage" | "theme" | "formatCreateLabel" | "menuPortalTarget" | "onInputChange">;

const customStyles = (small?: boolean, withIndicatorSeparator?: boolean): StylesConfig => ({
    ...selectStyles(small),
    indicatorSeparator: (base) => ({
        ...base,
        backgroundColor: Color.GREY,
        display: withIndicatorSeparator ? "block" : "none"
    })
});

const SelectAutocomplete: FunctionComponent<SelectAutocompleteProps> = (props) => {
    const {t} = useTranslation(TranslationLibFile.COMMON);
    const [selectedOption, setSelectedOption] = useState<SelectOption>();
    const {
        value,
        options,
        placeholder,
        onChange,
        onCreate,
        disabled,
        creatable = false,
        clearable = false,
        small,
        resetInputOnSelect = true,
        ...reactSelectProps
    } = props;

    useEffect(() => {
        setSelectedOption(getSelectedOption(options, value));
    }, [value, options]);

    const onInputChange = (inputValue: string, actionMeta: InputActionMeta) => {
        const {action, prevInputValue} = actionMeta;
        if (action === "input-change") {
            return inputValue;
        } else {
            return prevInputValue;
        }
    };

    return (
        <>
            {
                React.createElement(creatable ? CreatableReactSelect : ReactSelect, {
                    value: selectedOption || null,
                    options: options,
                    placeholder: placeholder || t(TranslationLibKey.PLACEHOLDER_SELECT_DEFAULT),
                    onChange: (option: unknown | null) => onChange && onChange(option ? (option as SelectOption) : undefined),
                    onInputChange: !resetInputOnSelect ? onInputChange : undefined,
                    onCreateOption: onCreate,
                    isDisabled: disabled,
                    isClearable: clearable,
                    components: selectComponents,
                    styles: customStyles(small, clearable && !!selectedOption),
                    noOptionsMessage: () => t(TranslationLibKey.MESSAGE_NO_RESULT),
                    theme: customTheme,
                    formatCreateLabel: (value) => t(TranslationLibKey.SELECT_OPTION_CREATE, {value}),
                    menuPortalTarget: document.querySelector("body"),
                    ...reactSelectProps
                })
            }
            <ValidationInput
                value={value ? value as string : ""}
                required={props.required}
                disabled={props.disabled}
                onInput={props.onInput}
                onInvalid={props.onInvalid}
            />
        </>
    );
};

export default SelectAutocomplete;
